import React, { useMemo } from "react";
import { MoveLeft, MoveRight } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "ui/ToggleGroup";
import { cn } from "util/cn";
import EntityCompactCard from "components/Entities/EntityCompactCard";
import { TEABATE_ALIGNMENT_DIMENSIONS } from "components/Posts/kindFields/TeabateFields";
import type { QuizTeabateSelection, QuizTeabateStance } from "types/quiz";
import type { QuizUiStrings } from "i18n/quizUi";

interface QuizAlignmentTileProps {
  ui: QuizUiStrings;
  teabate: QuizTeabateSelection;
  targetStance: QuizTeabateStance;
  currentViewerStance?: QuizTeabateStance | null;
}

const RESPONSE_OPTIONS = [-2, -1, 0, 1, 2] as const;
const IMPORTANCE_OPTIONS = [-2, -1, 0, 1, 2] as const;

const QuizAlignmentTile: React.FC<QuizAlignmentTileProps> = ({
  ui,
  teabate,
  targetStance,
  currentViewerStance = null,
}) => {
  const dimensionLabels = useMemo(() => {
    const normalizedDimension = String(teabate.post.properties?.dimension || "").trim().toLowerCase();
    if (!normalizedDimension) {
      return null;
    }

    const category = TEABATE_ALIGNMENT_DIMENSIONS.find((group) =>
      group.label1.toLowerCase() === normalizedDimension || group.label2.toLowerCase() === normalizedDimension
    );
    if (!category) {
      return null;
    }

    const isLabel1 = category.label1.toLowerCase() === normalizedDimension;
    return {
      disagreeLabel: isLabel1 ? category.label2 : category.label1,
      agreeLabel: isLabel1 ? category.label1 : category.label2,
    };
  }, [teabate.post.properties?.dimension]);

  return (
    <div className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal p-1 rounded-lg space-y-2">
      <div className="rounded-xl bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover p-2">
        <EntityCompactCard
          entityId={teabate.post_id ?? undefined}
          entityType="teabate"
          showActions={false}
          showMeta={false}
          compactCard={true}
          showVote={false}
          showLink={false}
        />
      </div>

      <div className="flex items-center justify-between gap-2 px-1">
        <span className="rounded-full bg-eoe-icon px-2 py-1 text-xs text-eoe-text-sub dark:bg-eoe-dark-icon dark:text-eoe-dark-text-sub">
          {teabate.issue.name}
        </span>
        {currentViewerStance ? (
          <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
            Your response
          </span>
        ) : null}
      </div>

      <div className="mb-4">
        <div className="relative flex justify-center items-center mb-1">
          {dimensionLabels ? (
            <>
              <div className="absolute left-1 flex items-center gap-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <MoveLeft className="size-3" />
                <span>{dimensionLabels.disagreeLabel}</span>
              </div>
              <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub font-semibold">
                {ui.stanceLabel}
              </span>
              <div className="absolute right-1 flex items-center gap-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <span>{dimensionLabels.agreeLabel}</span>
                <MoveRight className="size-3" />
              </div>
            </>
          ) : (
            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub font-semibold">{ui.stanceLabel}</p>
          )}
        </div>
        <ToggleGroup type="single" value={targetStance.response?.toString()} className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl">
          {RESPONSE_OPTIONS.map((option) => {
            const targetSelected = targetStance.response === option;
            const currentSelected = currentViewerStance?.response === option;
            const currentClass = currentSelected
              ? (targetSelected
                  ? "bg-eoe-entity-user dark:bg-eoe-entity-user border-[6px] sm:border-8 border-eoe-blue dark:border-eoe-blue"
                  : "bg-eoe-blue dark:bg-eoe-blue text-white dark:text-eoe-dark-text")
              : "";

            return (
              <ToggleGroupItem
                key={option}
                value={option.toString()}
                disabled={true}
                className={cn(
                  "flex-1 text-[9px] sm:text-xxs md:text-xs font-light leading-snug data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                  targetSelected && "bg-eoe-entity-user dark:bg-eoe-entity-user text-white dark:text-eoe-dark-text",
                  currentClass
                )}
                aria-label={option.toString()}
                data-state="off"
              >
                {option === -2 && ui.responseStronglyDisagree}
                {option === -1 && ui.responseDisagree}
                {option === 0 && ui.responseNeutral}
                {option === 1 && ui.responseAgree}
                {option === 2 && ui.responseStronglyAgree}
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      </div>

      <div className="mb-4">
        <div className="flex justify-center items-center mb-1">
          <p className="text-xs font-semibold text-center text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.importanceLabel}</p>
        </div>
        <ToggleGroup type="single" value={targetStance.importance?.toString()} className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl">
          {IMPORTANCE_OPTIONS.map((option) => {
            const targetSelected = targetStance.importance === option;
            const currentSelected = currentViewerStance?.importance === option;
            const currentClass = currentSelected
              ? (targetSelected
                  ? "bg-eoe-entity-user dark:bg-eoe-entity-user border-[6px] sm:border-8 border-eoe-blue dark:border-eoe-blue"
                  : "bg-eoe-blue dark:bg-eoe-blue text-white dark:text-eoe-dark-text")
              : "";

            return (
              <ToggleGroupItem
                key={option}
                value={option.toString()}
                disabled={true}
                className={cn(
                  "flex-1 text-[9px] sm:text-xxs md:text-xs font-light leading-snug data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                  targetSelected && "bg-eoe-entity-user dark:bg-eoe-entity-user text-white dark:text-eoe-dark-text",
                  currentClass
                )}
                aria-label={option.toString()}
                data-state="off"
              >
                {option === -2 && ui.importanceVeryLow}
                {option === -1 && ui.importanceLow}
                {option === 0 && ui.importanceNeutral}
                {option === 1 && ui.importanceHigh}
                {option === 2 && ui.importanceVeryHigh}
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      </div>
    </div>
  );
};

export default QuizAlignmentTile;
